import { InfiniteListBase, useGetList, useListContext } from "ra-core";
import { Mail, MailOpen, MousePointerClick } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Emails matched to this contact, both directions (decision #46/#47's
 * inbound BCC/forward capture, plus automation/workflow sends - decision
 * #94): reads activity_events directly, since neither ever touches
 * contact_notes. Renders nothing when there's no mail either direction, so
 * it adds no visual noise to the (currently: most) contacts who have none.
 *
 * Sent emails show "Opened" / "Clicked" when Postmark reported it (decision
 * #108): email.opened / email.clicked events carry the same messageId the
 * worker stored on email.sent.
 */

interface EmailEventPayload {
  direction?: "inbound" | "outbound";
  subject?: string;
  fromAddress?: string;
  toAddresses?: string[];
  bodyPreview?: string;
  messageId?: string;
}

interface ActivityEventRecord {
  id: string;
  occurred_at: string;
  payload: EmailEventPayload;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** messageId -> what the recipient did with that email. */
const useEngagement = (contactId: number | string) => {
  const { data } = useGetList<{ id: string; type: string; payload: { messageId?: string } }>(
    "activity_events",
    {
      filter: { contact_id: contactId, "type@in": "(email.opened,email.clicked)" },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "occurred_at", order: "DESC" },
    },
    { retry: false },
  );
  const byMessage = new Map<string, { opened: boolean; clicked: boolean }>();
  for (const e of data ?? []) {
    const id = e.payload?.messageId;
    if (!id) continue;
    const seen = byMessage.get(id) ?? { opened: false, clicked: false };
    if (e.type === "email.opened") seen.opened = true;
    if (e.type === "email.clicked") seen.clicked = true;
    byMessage.set(id, seen);
  }
  return byMessage;
};

const EmailActivityCard = ({ contactId }: { contactId: number | string }) => {
  const { data, isPending } = useListContext<ActivityEventRecord>();
  const engagement = useEngagement(contactId);
  if (isPending || !data || data.length === 0) return null;

  return (
    <Card>
      <CardContent>
        <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-3">
          Recent emails
        </h2>
        <div className="flex flex-col gap-2.5">
          {data.map((e) => (
            <div key={e.id} className="flex gap-2 text-sm border-t border-border pt-2.5 first:border-t-0 first:pt-0">
              <Mail className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium truncate">
                    {e.payload.subject || "(no subject)"}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0 ml-auto">
                    {fmtDate(e.occurred_at)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {e.payload.direction === "outbound"
                    ? `to ${e.payload.toAddresses?.join(", ") ?? ""}`
                    : `from ${e.payload.fromAddress}`}
                </p>
                {e.payload.bodyPreview && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {e.payload.bodyPreview}
                  </p>
                )}
                {(() => {
                  const seen = e.payload.messageId ? engagement.get(e.payload.messageId) : undefined;
                  if (!seen) return null;
                  return (
                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                      {seen.opened && (
                        <span className="inline-flex items-center gap-1">
                          <MailOpen className="w-3 h-3" /> Opened
                        </span>
                      )}
                      {seen.clicked && (
                        <span className="inline-flex items-center gap-1">
                          <MousePointerClick className="w-3 h-3" /> Clicked
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const ContactEmailActivity = ({ contactId }: { contactId: number | string }) => (
  <InfiniteListBase
    resource="activity_events"
    filter={{ contact_id: contactId, "type@in": "(email.received,email.sent)" }}
    sort={{ field: "occurred_at", order: "DESC" }}
    perPage={10}
    disableSyncWithLocation
    storeKey={false}
    loading={false}
    error={false}
    queryOptions={{
      onError: () => {
        // The demo/FakeRest provider has no activity_events collection - fail
        // silently there (the card just renders nothing) instead of a toast.
      },
    }}
  >
    <EmailActivityCard contactId={contactId} />
  </InfiniteListBase>
);
